/**
 * WHO moves the stock, for a given document.
 *
 * THE OWNER DECISION THIS ENCODES
 * ------------------------------
 *   Quotation → Sales Order → Delivery Note / Dispatch → Sales Invoice → Payment
 *
 * - **A DRAFT never moves stock.** Not an invoice, not a delivery note, nothing.
 *   A draft is a thought; stock leaves the shop when goods do.
 * - **The dispatch document owns the stock-out.** When a Delivery Note is
 *   issued, IT deducts. An invoice raised against that Delivery Note is a
 *   billing event and must not deduct again.
 * - **An invoice with no prior delivery MAY own the stock-out**, because many
 *   businesses never raise a Delivery Note and deliver-and-invoice in one step.
 *
 * This is deliberately NOT a blanket "ISSUED = stock movement" rule. That rule
 * is wrong in both directions: it moves stock for a draft, and it double-deducts
 * a delivered-and-invoiced sale. Both are the same defect — stock leaving the
 * shop twice for one set of goods — and neither is visible in a report, because
 * the arithmetic still balances.
 *
 * WHY IT IS A PURE FUNCTION
 * -------------------------
 * Two services post stock — the one that CREATES a document and the one that
 * ISSUES one — and they must never disagree. A rule that lives in both is a
 * rule that will drift, and the drift is a silently wrong stock balance. So both
 * call this, and the tests here are the only place the rules are written down.
 */
import { docSpec, type DocType } from './docTypes';
import type { DocStatus } from './lifecycle';

/**
 * Document types that physically move goods and therefore own the movement.
 *
 * These are the DISPATCH documents: issuing one of these means the goods left
 * the location, so any later document that consumes it must not move stock again.
 */
export const DISPATCH_DOC_TYPES: readonly DocType[] = ['DELIVERY_NOTE', 'PURCHASE_RECEIPT'];

/** True when issuing a document of this type is what actually moves the goods. */
export function isDispatchDocument(docType: DocType): boolean {
  return DISPATCH_DOC_TYPES.includes(docType);
}

/**
 * Who owns the stock movement for one line of one document.
 *
 * - `SELF`   — this document must post the movement.
 * - `DISPATCHED` — a Delivery Note (or Receipt) already moved it; do not deduct
 *   again. This is the double-deduction guard, and it is per LINE, not per
 *   document: a single invoice can legitimately have one line delivered and
 *   another delivered-and-invoiced in one step.
 * - `NONE`   — this document must not post anything.
 */
export type StockOwner = 'SELF' | 'DISPATCHED' | 'NONE';

/** Stock-ledger reason for a movement, by document type and direction. */
export type StockReason = 'SALE' | 'SALE_RETURN' | 'PURCHASE' | 'PURCHASE_RETURN';

/**
 * The ledger reason a document's movement is filed under.
 *
 * Shared, because the create path and the issue path both post movements and a
 * reason that differs between them would split one sale across two ledger
 * categories — invisible in a total, and wrong in every stock report grouped by
 * reason.
 */
export function movementReasonFor(docType: DocType, direction: 'IN' | 'OUT'): StockReason {
  if (docType === 'PURCHASE_RECEIPT') return 'PURCHASE';
  if (docType === 'PURCHASE_RETURN') return 'PURCHASE_RETURN';
  if (docType === 'SALES_RETURN') return 'SALE_RETURN';
  return direction === 'IN' ? 'PURCHASE' : 'SALE';
}

export interface StockOwnershipInput {
  readonly docType: DocType;
  readonly status: DocStatus;
  /**
   * `docType` of the document owning the line this one CONSUMES, or null when
   * the line has no source (a standalone invoice raised with no Sales Order).
   *
   * This is the field that distinguishes "invoice against a Delivery Note" from
   * "invoice straight from a Sales Order" — the two look identical in the link
   * relation and differ entirely in who owns the stock.
   */
  readonly sourceLineDocType?: DocType | null;
  /**
   * May an invoice with no prior delivery act as the dispatch document?
   *
   * The locked decision says an issued invoice with no prior delivery "MAY move
   * stock IF the invoice is being used as the dispatch document" — so it is
   * conditional, and this is the condition. A business that always raises a
   * Delivery Note sets it false, and then a direct invoice stops deducting stock
   * it never actually moved.
   *
   * Defaults to true when omitted, because most small businesses deliver and
   * invoice in one step and would otherwise silently stop tracking stock. Named
   * to match `FulfilmentSettings.allowInvoiceWithoutDelivery` deliberately: the
   * editor cap and the ledger posting must answer the same question, and two
   * differently-named flags for one setting is how they drift apart.
   */
  readonly allowInvoiceWithoutDelivery?: boolean;
}

/** The single rule deciding whether a document posts stock. */
export function stockOwnerFor(input: StockOwnershipInput): StockOwner {
  // 1. A draft is not an event. Nothing leaves the shop because a form was saved.
  if (input.status !== 'ISSUED') return 'NONE';

  // 2. Some documents never move goods: a quotation, a sales order, a credit note.
  if (!docSpec(input.docType).movesStock) return 'NONE';

  // 3. If the line consumes a dispatch document, the goods already left when
  //    THAT was issued. Deducting again is the double-deduction bug.
  const source = input.sourceLineDocType;
  if (source && isDispatchDocument(source)) return 'DISPATCHED';

  // 4. An invoice with nothing consumed is the dispatch document only if the
  //    business works that way. When they do not, this invoice is billing for
  //    goods that have not left yet, and deducting them would understate stock.
  if (input.docType === 'SALES_INVOICE' || input.docType === 'PURCHASE_INVOICE') {
    if (input.allowInvoiceWithoutDelivery === false) return 'NONE';
  }

  return 'SELF';
}

/**
 * The direction a movement must be posted in for this document.
 *
 * Cancellation is the case this exists for: an issued document's movements have
 * already been posted, and undoing the event means posting the OPPOSITE movement,
 * not deleting the original. The ledger is append-only by design, so a cancelled
 * sale is still visible in the history — it is simply neutralised.
 */
export function inverseDirection(direction: 'IN' | 'OUT'): 'IN' | 'OUT' {
  return direction === 'IN' ? 'OUT' : 'IN';
}
